import { ProposalsFilters } from "@client/components/proposals-filters";
import { prefetchProposalApplications } from "./prefetchProposalApplications";
import { preloadVideoPlayer } from "@client/components/video";
import { VideoStatesProvider } from "@client/components/video/VideoStatesProvider";
import { ProposalsProgressCard } from "@client/components/proposals-progress-card";
import { ProposalsStatusSummaryCards } from "@client/components/proposals-status-summary-cards";
import { ProposalsTableHeader } from "@client/components/proposals-table-header";
import { GeneralNotesDrawer } from "@client/components/round/GeneralNotes";
import { useMvfGeneralNotesRatingRefresh } from "@client/hooks/use-mvf-general-notes-rating-refresh";
import { TodoDrawer } from "@client/pages/round/funder-workspace/todo/TodoDrawer";
import { TodoRailSyncProvider } from "@client/pages/round/funder-workspace/todo/TodoRailSyncProvider";
import { RoundAdminNavbar } from "@client/components/round/RoundAdminNavbar";
import Container from "@client/components/utils/Container";
import { NotFoundCard } from "@client/components/utils/NotFoundCard";
import { RouteScroller } from "@client/components/utils/RouteScroller";
import { useIsOutOfDate } from "@client/components/utils/OutOfDateVersionWarning";
import { RoundPage } from "@client/components/utils/RoundPage";
import { RouteErrorBoundary } from "@client/components/utils/RouteErrorBoundary";
import { useSetHeaderConnection } from "@client/HeaderConnectionContext";
import {
  isIPadOfflineFailure,
  isTransportError,
  OFFLINE_CACHE_KEYS,
  readOfflineCache,
  withOfflineCache,
  writeOfflineCacheIfIPad,
} from "@client/offline/offlineCache";
import { getIsOnline } from "@client/hooks/useNetworkStatus";
import {
  ALL_STATUSES,
  ALL_TRACK_CATEGORIES,
} from "@client/hooks/use-my-evaluations-data";
import { useIsRestrictedFunder } from "@client/hooks/useIsRestrictedFunder";
import { usePageScrollRestoration } from "@client/hooks/use-page-scroll-restoration";
import {
  RECENT_IMPERSONATE_PREFIX,
  useSetImpersonate,
} from "@client/ImpersonateContext";
import {
  buildAuthHeadersForViewer,
  getClientBaseUrl,
  trpc,
  trpcVanilla,
  trpcVanillaForViewer,
  type TrpcVanillaClient,
} from "@client/trpc";
import {
  CACHE_FIRST_MAX_ACTION_GAP,
  isCacheFirstEnabled,
  recordObservedAggregateSize,
} from "@lib/cacheFirstRounds";
import { useDisableCachingActive } from "@client/offline/disableCachingStore";
import { type CurrentUser } from "@lib/auth/shared/types";
import { type RoundBootstrapData } from "@lib/routeTypes";
import { getQueryKey } from "@trpc/react-query";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { type UserProfiles } from "@lib/types";
import isTextEditingElement from "@client/utils/isTextEditingElement";
import { getDocumentTitleForRoundSubPath } from "@client/utils/pageTitle";
import { resolveProposalIdFromRouteSegment } from "@client/utils/proposalRoute";
import { Client, DISCARDED_ACTION_PREFIX } from "@engine/Client";
import { runBoundedCatchUpFetch } from "@engine/catchUpFetch";
import { type ActionMessage, roundReducer } from "@engine/core";
import { registerPostHogProperties } from "@client/telemetry/posthog";
import {
  ROUND_SYNC_EVENTS,
  captureRoundSyncEvent,
} from "@client/utils/syncAnalytics";
import {
  getActiveEvaluators,
  getFieldResponseReferences,
  isUserFunder,
} from "@engine/round/accessors";
import {
  type RoundAggregate,
  roundsCouldDifferInResult,
} from "@engine/round/aggregate";
import ClientInfoStore from "@engine/round/infoStore/ClientInfoStore";
import SPAStorageAdapterWrapper from "@engine/storageAdapters/SPAStorageAdapterWrapper";
import { type SProcessOutputWithMatchingAdjustment } from "@lib/algoInterface/runSProcessOnRound";
import { sProcessOutputForRound } from "@client/utils/sProcessOutput";
import { runWhenIdle } from "@client/utils/idle";
import {
  lazyWithBackgroundWarm,
  warmChunkNowForInitialPath,
} from "@client/utils/backgroundChunkWarming";
import { isIPadCapacitor } from "@client/lib/isIPad";
import * as Sentry from "@client/telemetry/sentry";
import { contentId } from "@lib/contentId";
import {
  isFunderLandingPath,
  roundLoadDefersFieldResponses,
} from "@lib/earlyRoundFetches";
import { isFunderReviewPath } from "@lib/funderWorkspaceRoutes";
import { generateClientId } from "@lib/utils/idGeneration";
import { Button, Skeleton, Table as TableEl } from "@sfc/ui";
import {
  createContext,
  Suspense,
  type ReactNode,
  startTransition,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useSyncExternalStore,
} from "react";
import { toast, type ToastT } from "sonner";

import { Redirect, Route, Switch, useLocation, useSearchParams } from "wouter";
import {
  type RoundData,
  type StateExternalStore,
  getGlobalImpersonation,
  impersonationRef,
  useEventSourcing,
  useImpersonationRefValue,
} from "../../ImperativeState";
import {
  ClientSettingsProvider,
  PinnedNodesProvider,
  SocketStatusProvider,
} from "./roundShellProviders";
import {
  prefetchWorkspaceMeetings,
  workspaceMediaPathTargeted,
  workspaceMeetingTargetFromSearch,
} from "./funder-workspace/prefetchWorkspaceMeetings";
import { prefetchWorkspaceCorpus } from "./funder-workspace/prefetchWorkspaceCorpus";
import {
  prefetchWorkspaceProposalClips,
  workspaceExcerptTargetFromSearch,
} from "./funder-workspace/prefetchWorkspaceProposalClips";
import { prefetchWorkspaceRecommenderClips } from "./funder-workspace/prefetchWorkspaceRecommenderClips";
import { humanizeActionPayload } from "./logs/humanizeActionPayload";
import {
  createStateContextStore,
  OutputContext,
  OutputRoundContext,
  StateContext,
  useActingAsUserId,
  useAppMode,
  useEffectiveIsSfcAdmin,
  useRound,
  useStateSelector,
  useUserProfiles,
} from "./round-context";
import {
  classifyUnreachableRoundLoad,
  getRoundLoadErrorMessage,
  isUnansweredStatus,
  isUnreachableRoundLoadError,
  RoundLoadError,
} from "./roundLoadError";

import { DispatchContext } from "./round-context";

const ProposalsTablePage = lazyWithBackgroundWarm(async () => {
  const { ProposalsPage } = await import("./Proposals");
  return { default: ProposalsPage };
});

const loadProposalDetailsPage = async () => ({
  default: (await import("./[roundId]/proposals/[proposalId]/page"))
    .ProposalDetailsPage,
});
const ProposalDetailsPage = lazyWithBackgroundWarm(loadProposalDetailsPage);

const loadProposalsPage = async () => ({
  default: (await import("./[roundId]/proposals/page")).ProposalsPage,
});
const ProposalsPage = lazyWithBackgroundWarm(loadProposalsPage);

warmChunkNowForInitialPath(
  ProposalDetailsPage.preload,
  (path) =>
    path.match(/^\/round\/[^/]+\/proposals(?:\/([^/]+))?\/?$/)?.[1] != null,
);
warmChunkNowForInitialPath(ProposalsPage.preload, (path) =>
  /^\/round\/[^/]+\/proposals\/?$/.test(path),
);

// The funder workspace is the biggest single route cluster (~570KB minified).
// It's lazied like the rest so non-funder routes never load it — but funder
// cold landings don't pay a serial chunk round trip either: the server
// preloads the workspace graph on funder routes (appShell.ts), and this
// initial-path warm starts evaluating it as soon as the entry executes.
const loadFunderWorkspacePage = async () => ({
  default: (await import("./FunderWorkspacePage")).FunderWorkspacePage,
});
const FunderWorkspacePage = lazyWithBackgroundWarm(loadFunderWorkspacePage);
// Non-funders on these paths pay only timing, not bytes: the idle warm
// fetches every chunk soon after startup regardless.
warmChunkNowForInitialPath(FunderWorkspacePage.preload, isFunderLandingPath);

// Route-level code splitting for every page that is NOT on the funder's
// landing path (SP: entry-bundle work). Each lazy() below moves that page —
// and everything only it reaches (d3-sankey under funding-flow, the MVF
// graph stack under comparisons, the admin surfaces) — out of the entry
// bundle's startup parse. The funder workspace is also split (see its
// declaration above for how funder landings avoid the serial cost). A single
// Suspense around the route <Switch> serves all of these; the proposals
// table keeps its more specific skeleton fallback. lazyWithBackgroundWarm
// (vs bare lazy) re-fetches every one of these chunks in the background
// once the first page has loaded and gone idle, so splitting never costs
// offline coverage or post-deploy chunk consistency.
const DataSyncing = lazyWithBackgroundWarm(
  () => import("../admin/DataSyncing"),
);
const ProposalForms = lazyWithBackgroundWarm(
  () => import("../admin/ProposalForms"),
);
const RoundMembers = lazyWithBackgroundWarm(
  () => import("../admin/RoundMembers"),
);
const AnonymizationPage = lazyWithBackgroundWarm(async () => ({
  default: (await import("./[roundId]/admin/anonymization/page"))
    .AnonymizationPage,
}));
const FactCheckResponsesPage = lazyWithBackgroundWarm(async () => ({
  default: (await import("./[roundId]/admin/fact-check-responses/page"))
    .FactCheckResponsesPage,
}));
const RecommenderActivityPage = lazyWithBackgroundWarm(async () => ({
  default: (await import("./[roundId]/admin/recommender-activity/page"))
    .RecommenderActivityPage,
}));
const TrackSettingsPage = lazyWithBackgroundWarm(async () => ({
  default: (await import("./[roundId]/admin/track-settings/page"))
    .TrackSettingsPage,
}));
const ComparisonsPage = lazyWithBackgroundWarm(async () => ({
  default: (await import("./[roundId]/comparisons/page")).ComparisonsPage,
}));
const DisagreementsPage = lazyWithBackgroundWarm(async () => ({
  default: (await import("./[roundId]/disagreements/page")).DisagreementsPage,
}));
const FundingFlowMatrixPage = lazyWithBackgroundWarm(async () => ({
  default: (await import("./[roundId]/funding-flow/page"))
    .FundingFlowMatrixPage,
}));
const ExportData = lazyWithBackgroundWarm(async () => ({
  default: (await import("./ExportData")).ExportData,
}));
const FunderSettings = lazyWithBackgroundWarm(async () => ({
  default: (await import("./FunderSettings")).FunderSettings,
}));
const Investigations = lazyWithBackgroundWarm(() => import("./Investigations"));
const Logs = lazyWithBackgroundWarm(async () => ({
  default: (await import("./logs/Logs")).Logs,
}));
const MembersPage = lazyWithBackgroundWarm(async () => ({
  default: (await import("./Members")).MembersPage,
}));
const Onboarding = lazyWithBackgroundWarm(() => import("./Onboarding"));
const Recusals = lazyWithBackgroundWarm(() => import("./Recusals"));
const RoundSettings = lazyWithBackgroundWarm(async () => ({
  default: (await import("./RoundSettings")).RoundSettings,
}));
const SfcRoundSettings = lazyWithBackgroundWarm(async () => ({
  default: (await import("./RoundSettings")).SfcRoundSettings,
}));
const Settings = lazyWithBackgroundWarm(() => import("./Settings.js"));
const SpeculationGrants = lazyWithBackgroundWarm(async () => ({
  default: (await import("./SpeculationGrants")).SpeculationGrants,
}));

function LogsRoute() {
  const round = useRound();
  const storage = useStateSelector((state) => state.storage);
  return (
    <Logs round={round} lastActionNumber={storage.getLastConfirmedVersion()} />
  );
}

/**
 * Keeps the registered viewer-role property in step with the LIVE aggregate.
 *
 * The initial registration happens once per round load, but membership actions
 * (`CREATE_OR_UPDATE_ROUND_MEMBER`, removals) grant or revoke the funder role
 * mid-session without rerunning the load — so without this, confirmation events
 * would keep carrying the role the viewer had when they arrived. Impersonation
 * switches change the effective user the same way, which is why the acting user is
 * a dependency too.
 *
 * Sits beside ImpersonateSync for the same reason: an always-mounted sync effect
 * inside the round provider, where the live round state is available.
 */
function ViewerRoleSync() {
  const round = useRound();
  const actingAsUserId = useActingAsUserId();
  const viewerIsFunder = actingAsUserId
    ? isUserFunder(round, actingAsUserId)
    : false;
  useEffect(() => {
    registerPostHogProperties({ round_viewer_is_funder: viewerIsFunder });
  }, [viewerIsFunder]);
  return null;
}

function ImpersonateSync() {
  const round = useRound();
  const mode = useAppMode();
  const setMode = useStateSelector((state) => state.setMode);
  const underlyingUserId = useStateSelector((state) => state.underlyingUserId);
  const underlyingUserIsSfcAdmin = useStateSelector(
    (state) => state.underlyingUserIsSfcAdmin,
  );
  const userProfiles = useUserProfiles();
  const setImpersonate = useSetImpersonate();
  useEffect(() => {
    setImpersonate({
      mode,
      setMode,
      round,
      underlyingUserId,
      underlyingUserIsSfcAdmin,
      userProfiles,
    });
    return () => setImpersonate(null);
  }, [
    mode,
    setMode,
    round,
    underlyingUserId,
    underlyingUserIsSfcAdmin,
    userProfiles,
    setImpersonate,
  ]);

  // Track recently impersonated users in localStorage so the Settings page
  // can show them at the top. This runs here (not in Settings) because
  // impersonation can be triggered from the header mask icon on any page.
  useEffect(() => {
    if (mode.viewAsUserId) {
      try {
        const key = `${RECENT_IMPERSONATE_PREFIX}${round.id}`;
        const existing: string[] = JSON.parse(
          localStorage.getItem(key) ?? "[]",
        );
        const updated = [
          mode.viewAsUserId,
          ...existing.filter((id) => id !== mode.viewAsUserId),
        ].slice(0, 10);
        localStorage.setItem(key, JSON.stringify(updated));
      } catch {
        // localStorage errors are non-critical
      }
    }
  }, [mode.viewAsUserId, round.id]);

  return null;
}

// Moved to ./clientSettingsContext (leaf module — see the cycle note there);
// re-exported here for existing importers.
export {
  ClientSettingsContext,
  getStartingSimulationHorizon,
} from "./clientSettingsContext";

function handleError(e: string) {
  // Coalesce discarded-action notices by message so a burst of *identical*
  // server rejections (e.g. several queued offline edits failing the same way on
  // reconnect) collapses into one toast — while distinct reasons each surface,
  // since the reason is the useful debugging signal.
  if (e.startsWith(DISCARDED_ACTION_PREFIX)) {
    toast.error(e, { id: `action-discarded:${e}` });
    return;
  }
  toast.error(e);
}

// `impersonatedUserId` scopes the request AND the size record to one viewer:
// loadRoundData passes its load-time snapshot so every download of a load —
// including the adapter's retry/fallback calls — matches the viewer the rest
// of that load (adapter cache key, late cache-write gate) resolved. Undefined
// means snapshot the ref now (standalone callers outside a load).
// Backoff between automatic retries of a round load that never reached the
// server. Length caps the attempts; the total (~6s) is sized to outlast a
// rolling deploy's cutover without leaving someone staring at a skeleton.
const AUTO_RETRY_DELAYS_MS = [500, 1500, 4000];

async function downloadRound(
  roundId: string,
  impersonatedUserId: string | null = impersonationRef.current,
): Promise<RoundAggregate> {
  // Hit the Express byte-cache route rather than tRPC's getAggregate: tRPC
  // re-serializes its return value, so it can't serve the precomputed gzipped
  // aggregate. `buildAuthHeadersForViewer` carries impersonation + native auth.
  // browser negotiates and transparently inflates gzip via Accept-Encoding.
  //
  // `credentials: "same-origin"` (matching the tRPC client's default) sends the
  // session cookie on web, where this is a same-origin request. On bundled
  // native builds the request is cross-origin to the API host, whose CORS
  // policy deliberately does NOT allow credentials — native auth rides the
  // bearer token in the auth headers instead. Using "include" here would
  // make the browser reject those native responses at the CORS check.
  const url = `${getClientBaseUrl()}/api/round-aggregate?roundId=${encodeURIComponent(
    roundId,
  )}`;
  const response = await fetch(url, {
    method: "GET",
    credentials: "same-origin",
    headers: buildAuthHeadersForViewer(impersonatedUserId),
  });
  if (response.status === 404) {
    throw new RoundLoadError("not-found", "Round not found");
  }
  if (response.status === 401 || response.status === 403) {
    throw new RoundLoadError(
      "forbidden",
      "You do not have access to this round",
    );
  }
  if (!response.ok) {
    // A gateway-style status (502/503/504, or a timeout/rate limit) is a deploy
    // gap, a restarting dev backend, or an unavailable database — not a broken
    // round. Classified as "connection" so it reads as transient to the person
    // AND auto-retries below, instead of dead-ending on "Failed to load round
    // data" for a server that is about to be fine. A plain 500 is NOT in that
    // set: this endpoint raises one itself when the aggregate genuinely fails
    // to build, and that keeps its own message and its single attempt.
    if (isUnansweredStatus(response.status)) {
      throw new RoundLoadError(
        "connection",
        `Couldn't reach the server (status ${response.status})`,
      );
    }
    throw new RoundLoadError(
      "generic",
      `Failed to load round data (status ${response.status})`,
    );
  }
  // Read as text so the payload size can be recorded for the learned
  // cache-first decision (see cacheFirstRounds.ts).
  const text = await response.text();
  recordObservedAggregateSize(roundId, impersonatedUserId, text.length);
  return JSON.parse(text) as RoundAggregate;
}

async function downloadActionsById(
  roundId: string,
  ids: string[],
  client: TrpcVanillaClient = trpcVanilla,
): Promise<{ [id: string]: ActionMessage }> {
  const result = await runBoundedCatchUpFetch((signal) =>
    client.getAggregateActionsById.query({ roundId, ids }, { signal }),
  );
  if (result === "round-member-not-found") {
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
    throw new Error("User is not a member of this round");
  }
  return result;
}

async function loadRoundData(
  clientId: string,
  roundId: string,
  refetchUserProfiles: () => void,
  queryClient: QueryClient,
  // True once the effect that started this load has been superseded (round
  // change, impersonation switch, unmount). Checked by the late cache-write
  // gate: an obsolete load must not write the aggregate cache — its row would
  // clobber the replacement load's (one row per round) and defeat the next
  // visit's fast path.
  isAbandoned: () => boolean = () => false,
  // Dev-only "Disable round cache" override (disableCachingStore, admin-gated
  // by the caller): true forces this load down the fresh-download path. Only
  // the cache-first READ is bypassed — the download still persists to the
  // cache below, so switching the override off returns to a warm fast path.
  bypassRoundCache: boolean = false,
): Promise<RoundData> {
  // Timed from the very top, because this is the wait the funder actually sits
  // through: the bootstrap and profile requests below, the aggregate download,
  // adapter init, and the field responses after it. A timer started inside
  // SPAStorageAdapter.init would exclude most of that and report a round open as
  // fast while the screen was still empty.
  const loadStartedAt = performance.now();
  // Snapshotted once so the load decision, and the late-evaluated cache-write
  // gate below, resolve the same viewer this load was made for.
  const impersonatedUserIdAtLoad = impersonationRef.current;
  // THE cache-read choke point (same rule as the Force streaming override):
  // everything cache-first hangs off this one flag — the skipped eager
  // download, the bootstrap branch, the adapter's cacheFirst option, and the
  // reported `cache_first_eligible`. Overriding here therefore also keeps the
  // field RUM series honest: a bypassed open reports eligible=false and never
  // counts as a cache miss. Do not gate individual consumers piecemeal.
  const cacheFirstEnabled =
    !bypassRoundCache && isCacheFirstEnabled(roundId, impersonatedUserIdAtLoad);
  // Every request of THIS load carries the load's viewer snapshot — the
  // aggregate downloads below, and every tRPC call whose response is
  // anonymized per viewer (bootstrap, profiles, actions, field responses) —
  // so the payloads, the size record, and the cache identities this load
  // writes under can never mix viewers if an admin switches impersonation
  // mid-load. (getCurrentUser/getCurrentUserId stay on the shared client:
  // they resolve the underlying login, which impersonation never changes.)
  const downloadRoundForLoad = (id: string) =>
    downloadRound(id, impersonatedUserIdAtLoad);
  const trpcForLoad = trpcVanillaForViewer(impersonatedUserIdAtLoad);

  // Start the aggregate download immediately — it only needs roundId and is
  // the slowest call. The storage adapter will consume it via the callback
  // instead of starting a second request.  Suppress unhandled rejection in
  // case user queries fail first and we never reach the storage adapter init.
  //
  // Cache-first rounds skip this eager download entirely: the storage adapter
  // decides inside `init` whether to render from the IndexedDB cache (and let
  // the socket catch up) or download fresh. Eagerly downloading here would both
  // waste the transfer and, via the gate below, block the load on it.
  const aggregatePromise = cacheFirstEnabled
    ? null
    : downloadRoundForLoad(roundId);
  aggregatePromise?.catch(() => {});

  // Construct the field-response store up front (it has no dependencies) so a
  // COLD client can start fetching every current field response now — the
  // server derives the version ids from roundId alone — instead of waiting for
  // the aggregate to arrive and name them (~200ms later on the measured load).
  // No-op on a warm client: the store already holds the text in IndexedDB.
  const infoStore = new ClientInfoStore(
    async (fieldResponsesIds) => {
      return await trpcForLoad.getFieldResponses.query({
        roundId,
        fieldResponsesIds,
      });
    },
    undefined,
    // Tolerate field-response fetch failures when offline or the backend is
    // unreachable (SP-2612); errors the server sent must surface rather than
    // render incomplete proposals.
    (error) => !getIsOnline() || isTransportError(error),
  );
  // Cold-client warm-up of the full proposal-text payload — except on Review
  // URLs, where nothing on the route renders field responses and the megabyte
  // transfer would occupy the same connection as the lead video, subtitles,
  // and corpus payloads (the shell's early fetch skips it there for the same
  // reason, see lib/earlyRoundFetches.ts). The deferred hydration below
  // fetches it once the pageload's contended window has passed.
  const isReviewPathLoad =
    typeof window !== "undefined" &&
    isFunderReviewPath(roundId, window.location.pathname);
  if (!isReviewPathLoad) {
    void infoStore.prefetchAllCurrentIfCold(() =>
      trpcForLoad.getFieldResponses.query({ roundId }),
    );
  }

  let underlyingUserId: string;
  let isSfcAdmin: boolean;
  let effectiveUserId: string;
  let userProfiles: UserProfiles;
  // The server's current round version, captured for cache-first rounds so the
  // storage adapter can decide cache-vs-download without a second round trip.
  let currentVersion: number | null = null;
  // Whether the effective viewer is a speculator — resolved during the load
  // (bootstrap field or the parallel burst below) so the proposal page's
  // Speculation tab is decided before first paint instead of flashing in.
  // False when it can't be fetched (offline boots, agent sessions): the tab's
  // data can't load there either, and the discussion routes re-check
  // eligibility server-side regardless.
  let viewerIsSpeculator = false;

  if (cacheFirstEnabled) {
    // Single bootstrap round trip — currentUser + userProfiles + version in one
    // call — instead of three serial requests. This one RTT is the floor for a
    // permission-safe load; the large aggregate download is skipped when the
    // cached copy is within the version gap.
    //
    // On the iPad this call is the only network dependency of a cache-first
    // load, so it gets the same offline treatment the non-cache-first branch
    // gives getCurrentUser / getUserProfiles: tolerate the failure only when
    // the device is genuinely offline (an online 5xx/403 must surface), and
    // serve identity + profiles from the offline cache instead. The aggregate
    // itself falls back inside the storage adapter (its IndexedDB cache).
    // The impersonation snapshot (`impersonatedUserIdAtLoad`, taken before the
    // request goes out) matters here: trpcVanilla builds the
    // x-acting-as-user-id header from the same ref at send time, so the
    // profiles in the response are anonymized for THIS viewer. Reading the ref
    // again after the await could disagree with the payload if an admin
    // switches impersonation mid-flight — and would then file the old viewer's
    // profiles under the new viewer's offline-cache key below.
    let bootstrap: RoundBootstrapData | null = null;
    let bootstrapError: unknown = null;
    try {
      bootstrap = await trpcForLoad.getRoundBootstrap.query({ roundId });
    } catch (err) {
      if (!isIPadOfflineFailure(err)) throw err;
      bootstrapError = err;
    }
    if (bootstrap != null) {
      const uid = bootstrap.currentUser.userId;
      if (!uid) throw new Error("Must be logged in");
      underlyingUserId = uid;
      isSfcAdmin = bootstrap.currentUser.isSfcAdmin;
      effectiveUserId = impersonatedUserIdAtLoad ?? uid;
      userProfiles = bootstrap.userProfiles;
      currentVersion = bootstrap.version;
      viewerIsSpeculator = bootstrap.viewerIsSpeculator;
      // Mirror the pieces into the same offline-cache entries the
      // non-cache-first iPad path maintains (no-ops off the iPad), so offline
      // boots — and the AuthWrapper / useCurrentUser offline fallbacks, which
      // read the currentUser entry — keep working when every online load of
      // this round goes through the bootstrap call instead of the wrapped
      // getCurrentUser / getUserProfiles fetches.
      writeOfflineCacheIfIPad(
        OFFLINE_CACHE_KEYS.currentUser,
        bootstrap.currentUser,
      );
      writeOfflineCacheIfIPad(OFFLINE_CACHE_KEYS.currentUserId, uid);
      writeOfflineCacheIfIPad(
        OFFLINE_CACHE_KEYS.userProfiles(roundId, effectiveUserId),
        userProfiles,
      );
    } else {
      // iPad offline boot on a cache-first round: identity + profiles from the
      // offline cache (the entries mirrored above, or written by an earlier
      // non-cache-first load). Same uid preference as the non-cache-first
      // branch: the separately-cached id first, then the cached currentUser.
      // If either entry is missing the round was never fully loaded online —
      // surface the original network error, matching the wrapped-fetch path.
      const cachedUser = readOfflineCache<CurrentUser>(
        OFFLINE_CACHE_KEYS.currentUser,
      );
      const uid =
        readOfflineCache<string>(OFFLINE_CACHE_KEYS.currentUserId) ??
        cachedUser?.userId;
      if (cachedUser == null || !uid) throw bootstrapError;
      underlyingUserId = uid;
      isSfcAdmin = cachedUser.isSfcAdmin;
      // Same pre-request impersonation snapshot as the online arm, so one load
      // attempt resolves one coherent viewer identity throughout.
      effectiveUserId = impersonatedUserIdAtLoad ?? uid;
      const cachedProfiles = readOfflineCache<UserProfiles>(
        OFFLINE_CACHE_KEYS.userProfiles(roundId, effectiveUserId),
      );
      if (cachedProfiles == null) throw bootstrapError;
      userProfiles = cachedProfiles;
      // `currentVersion` stays null: the adapter skips the cache-first version
      // check while offline and serves its cached aggregate via the offline
      // fallback instead, so no version comparison happens on this path.
    }
  } else {
    // On web, start the user-profiles fetch now, in parallel with the aggregate
    // + auth burst below, instead of waiting for the aggregate to resolve first.
    // The request only needs roundId, so there's no data dependency forcing it
    // to run last, and we still await `aggregateGate` before consuming the result
    // below, so an aggregate rejection ("round not found") still surfaces ahead
    // of any profiles permission error. Suppress unhandled rejection in case an
    // earlier await throws before we reach the profiles gate (same pattern as
    // `aggregatePromise`).
    //
    // This is gated to web on purpose. There `withOfflineCache` is a pure
    // pass-through, so pre-starting changes nothing about caching or error
    // handling. On the iPad the wrapper both (a) keys the cached profiles by the
    // effective user and (b) decides offline-vs-online failures via
    // isIPadOffline() in its catch — both of which assume the request runs
    // *inside* the wrapper, right after the effective user is known. Pre-starting
    // there would let a mid-load impersonation switch mis-key the cache, and
    // would divorce the offline-failure check from the moment the request
    // actually failed. So the iPad keeps the original in-wrapper fetch untouched.
    const earlyUserProfilesPromise = isIPadCapacitor()
      ? null
      : trpcForLoad.getUserProfiles.query({ roundId });
    earlyUserProfilesPromise?.catch(() => {});

    // When the iPad is offline the aggregate download fails here, but the
    // storage adapter falls back to its IndexedDB cache. Tolerate the rejection
    // at the gate so we reach that fallback instead of failing the whole load.
    const aggregateGate = aggregatePromise
      ? aggregatePromise.catch((err: unknown) => {
          if (isIPadOfflineFailure(err)) return null;
          throw err;
        })
      : Promise.resolve(null);

    // getCurrentUser{,Id}/getUserProfiles are served from the iPad offline cache
    // when the device is offline (no-op pass-through on desktop/iPhone).
    //
    // getCurrentUserId and getCurrentUser both return the *underlying* user id, so
    // currentUser.userId is enough on its own. We only ALSO fetch (and separately
    // cache) the id on the iPad: it guards against a valid cached aggregate being
    // unopenable offline just because the currentUser cache entry didn't write.
    // On desktop/web that second round-trip is pure waste, so skip it there.
    const cachedUserIdPromise = isIPadCapacitor()
      ? withOfflineCache(OFFLINE_CACHE_KEYS.currentUserId, () =>
          trpcVanilla.getCurrentUserId.query(),
        ).catch(() => null)
      : Promise.resolve<string | null>(null);
    // The useCurrentUser hook already fetches this identity through React Query
    // higher in the tree. On web, reuse that shared cache entry (same query key)
    // so the boot doesn't fire a second, identical getCurrentUser request — if the
    // hook's query is in-flight or still fresh, fetchQuery returns it without a
    // network round-trip. The iPad keeps its localStorage offline-cache path
    // (the hook's offline fallback reads the same key), so only web is rerouted.
    const currentUserPromise = isIPadCapacitor()
      ? withOfflineCache(OFFLINE_CACHE_KEYS.currentUser, () =>
          trpcVanilla.getCurrentUser.query(),
        )
      : queryClient.fetchQuery({
          queryKey: getQueryKey(trpc.getCurrentUser, undefined, "query"),
          queryFn: () => trpcVanilla.getCurrentUser.query(),
          staleTime: 5 * 60 * 1000,
        });
    // Joins the burst rather than running after it — the Speculation-tab
    // fact must be settled before first paint, and a failure (offline, agent
    // session) just leaves the tab hidden rather than failing the load.
    const viewerIsSpeculatorPromise = trpcForLoad.getViewerIsSpeculator
      .query()
      .catch(() => false);
    const [, cachedUserId, currentUser, viewerIsSpeculatorResult] =
      await Promise.all([
        aggregateGate,
        cachedUserIdPromise,
        currentUserPromise,
        viewerIsSpeculatorPromise,
      ]);
    viewerIsSpeculator = viewerIsSpeculatorResult;
    const uid = cachedUserId ?? currentUser.userId;
    if (!uid) throw new Error("Must be logged in");
    underlyingUserId = uid;
    isSfcAdmin = currentUser.isSfcAdmin;
    // The effective (impersonated) user — used for the profile lookup, the
    // viewer identity, AND the offline-cache key below (getUserProfiles is
    // anonymized per effective user, so its cache must be scoped to that user).
    effectiveUserId = impersonatedUserIdAtLoad ?? uid;
    userProfiles = await withOfflineCache(
      OFFLINE_CACHE_KEYS.userProfiles(roundId, effectiveUserId),
      // Web reuses the request started in parallel above; the iPad starts it
      // here (inside the wrapper) so its cache keying + offline-failure
      // detection stay exactly as before.
      () =>
        earlyUserProfilesPromise ??
        trpcForLoad.getUserProfiles.query({ roundId }),
    );
  }

  const userProfile = userProfiles[effectiveUserId];
  if (!userProfile && !isSfcAdmin) {
    throw new Error("User not found in user profiles for round");
  }

  const userProfileHash = contentId(userProfiles);

  // An abandoned load must not touch the aggregate cache at all — not even
  // reads: its cache-first read would judge the replacement load's row a
  // viewer mismatch and DELETE it (the write gate below only stops writes),
  // costing the next visit its fast path. The effect that abandoned this load
  // discards its result and error alike, so bailing here is invisible.
  if (isAbandoned()) {
    throw new Error("Round load superseded before storage init");
  }
  let storage: SPAStorageAdapterWrapper;
  try {
    storage = await SPAStorageAdapterWrapper.init(
      roundReducer,
      roundId,
      userProfileHash,
      // Cache-first rounds call `downloadRound` only on a cache miss/too-stale,
      // so there's no eager fetch to reuse — just download fresh when asked.
      // Other rounds reuse the in-flight initial fetch on the first call (shared
      // with `aggregateGate` so the load doesn't double-fetch); a retry — init's
      // reconnect-mid-load path — must do a FRESH getAggregate rather than
      // re-await the already-settled (rejected) promise, which would defeat the
      // retry and fail the load even though the network is back.
      cacheFirstEnabled
        ? downloadRoundForLoad
        : (() => {
            let usedInitial = false;
            return (id: string) => {
              if (!usedInitial && aggregatePromise) {
                usedInitial = true;
                return aggregatePromise;
              }
              return downloadRoundForLoad(id);
            };
          })(),
      (id, ids) => downloadActionsById(id, ids, trpcForLoad),
      async (aggregateId, versions) => {
        if (aggregateId !== roundId) {
          throw new Error("Invalid aggregate ID");
        }
        const result = await runBoundedCatchUpFetch((signal) =>
          trpcForLoad.getAggregateActionsByVersion.query(
            { roundId, versions },
            { signal },
          ),
        );
        if (result === "round-member-not-found") {
          if (typeof window !== "undefined") {
            window.location.href = "/";
          }
          throw new Error("User is not a member of this round");
        }
        return result;
      },
      infoStore,
      userProfile?.track ?? "NO_TRACK",
      effectiveUserId,
      downloadRoundForLoad,
      // Cache writes are enabled on the iPad shell (offline support) and on
      // cache-first rounds (so the cache populates for fast warm loads).
      // `enabled` is a FUNCTION so the adapter re-evaluates it after the
      // download: a round that first qualifies during this very load — the
      // learned-size record happens inside downloadRound — persists now and
      // is fast on the NEXT visit, instead of paying one more full download.
      // `isOffline` uses the same `getIsOnline()` signal as the rest of the
      // offline UX (rather than the engine's default raw `navigator.onLine`) so
      // there is a single source of offline truth — equivalent in prod, and
      // honored by the `VITE_E2E_FORCE_OFFLINE`/`e2eForceOffline` test seams on
      // the simulator. `cacheFirst` (set only for opted-in rounds) makes `init`
      // try the cache before downloading, gated on the server's current version.
      {
        enabled: () =>
          !isAbandoned() &&
          (isIPadCapacitor() ||
            cacheFirstEnabled ||
            isCacheFirstEnabled(roundId, impersonatedUserIdAtLoad)),
        isOffline: () => !getIsOnline(),
        // Aggregate download failures with no server response (fetch
        // TypeError: backend unreachable) may serve the cache too. SP-2612.
        isUnreachableError: isTransportError,
        cacheFirst: cacheFirstEnabled
          ? {
              // Already fetched in the bootstrap call above — no extra round
              // trip; the adapter just compares it to the cached version.
              getCurrentVersion: () => Promise.resolve(currentVersion),
              maxActionGap: CACHE_FIRST_MAX_ACTION_GAP,
            }
          : undefined,
      },
      // Owner of the cached aggregates + queued offline edits. Wiped on init if
      // a different underlying account opens this device without signing out, so
      // one user's queued edits can't be flushed under another's session.
      underlyingUserId,
    );
  } catch (err) {
    // Unreachable-server failure on the iPad, with no usable cached round.
    // The common offline case deserves the actionable "not available offline
    // yet" message; the online-but-unreachable case (a woken iPad's first
    // fetch losing a race with a deploy or its own network re-association)
    // gets an honest connection message instead — see roundLoadError.ts. A
    // genuine storage/replay fault (corrupt IndexedDB, quota, a bad
    // unconfirmed-action replay) lands here too — don't silently swallow it
    // behind the friendly message. Log the underlying error first (the mobile
    // build has no Sentry, so console is the trace).
    if (isIPadOfflineFailure(err) && !(err instanceof RoundLoadError)) {
      console.error("[offline] round load failed offline:", err);
      throw classifyUnreachableRoundLoad(getIsOnline());
    }
    throw err;
  }

  const agg = storage.getUnconfirmedAggregateSync();

  // Used for the round-open event below. The SESSION-wide registration of this
  // role is not done here — ViewerRoleSync drives it from the live aggregate, so
  // it also survives a membership change mid-session, and a superseded load can't
  // leave a stale label behind (registration follows the mounted provider rather
  // than a load that may already have been replaced).
  const viewerIsFunder = isUserFunder(agg, effectiveUserId);

  const requiredVersions = getFieldResponseReferences(agg);
  const hydrateFieldResponses = async () => {
    await infoStore.ensureFieldResponses(requiredVersions);
    if (
      isIPadCapacitor() &&
      impersonationRef.current === impersonatedUserIdAtLoad
    ) {
      // Supplementary source queries have their own loading states. Warm them
      // without holding the round-open gate on every application in the round.
      void prefetchProposalApplications(
        agg,
        queryClient,
        trpcForLoad.getProposalApplication.query,
      ).catch((error) => {
        console.warn("Could not warm proposal applications", error);
      });
    }
    // Don't gc field responses on the iPad: the table is global across rounds, so
    // narrowing to this round's versions would delete OTHER previously-loaded
    // rounds' proposal text, leaving them incomplete (placeholders) offline. Keep
    // them so every cached round stays fully readable offline. (Desktop/web still
    // gc to bound the cache, since they don't rely on multi-round offline.)
    if (!isIPadCapacitor()) {
      // Queued edits outlive this load (and schema deploys); their before/after
      // versions are replay context the aggregate no longer references, so the
      // keep-set must carry them or a later offline open cannot hydrate them.
      // Housekeeping only: if the queue scan fails, SKIP the sweep — running
      // it with a partial keep-set would delete the rows the scan failed to
      // protect, and failing the load would hide a downloaded round over a
      // rebuildable cache. Unswept rows are retried next load.
      let queuedDependencies: string[];
      try {
        queuedDependencies = await storage.getQueuedFieldResponseDependencies();
      } catch (error) {
        console.error(
          "Skipping field-response gc: the queued-edit scan failed",
          error,
        );
        return;
      }
      const keep = new Set(requiredVersions.map((v) => v.version));
      for (const version of queuedDependencies) {
        keep.add(version);
      }
      await infoStore.gc(keep);
    }
  };
  // The funder workspace renders no proposal field responses, so on loads that
  // land there the megabyte-scale proposal-text hydration must not gate the
  // round becoming usable (measured at 170ms RTT: the full-round body streamed
  // for ~330ms across the window where the shell's JS, bootstrap, and
  // lead-video bytes contend for the link; on slower links it dominates
  // time-to-content). That is every Review URL, plus — for a funder of an SFC
  // round — the entry routes that redirect funders into the workspace: the
  // round root and bare /funder (bookmarks, home links, the iPad reopen path).
  // The hydration still runs — started here, consumed by the store's
  // subscribers — so an in-session hop to Proposals or a proposal dialog finds
  // the text loaded or loading. The cost of not awaiting: a failure no longer
  // fails the round load, so surface it to Sentry instead — a funder who LATER
  // leaves the workspace for a proposal surface could otherwise see
  // placeholder text with no recorded cause. Every other route keeps the
  // awaited gate: those surfaces render proposal text directly, and rendering
  // them incomplete is worse than rendering them late.
  const deferFieldResponses =
    typeof window !== "undefined" &&
    roundLoadDefersFieldResponses(roundId, window.location.pathname, {
      viewerIsFunder,
      isSfcRound: agg.isSfcRound,
    });
  if (deferFieldResponses) {
    // Bounded retry, because the consumer hooks only read and subscribe —
    // they never fetch — so a hydration that fails and is never retried
    // strands every later proposal surface on placeholder text for the
    // session. Three spaced attempts cover the realistic failure (a
    // transient blip on this one request, moments after the rest of the
    // load succeeded on the same connection). A failure that survives all
    // of them means the network is genuinely down — where the pre-deferral
    // behavior was a failed round load — so it reports to Sentry and stops:
    // recovery past that point is the user reloading. Bounded on purpose;
    // an unbudgeted retry loop must never be the recovery mechanism.
    void (async () => {
      // On Review URLs the eager transfers were skipped entirely (the shell's
      // early fetch and the cold-client prefetch above), so this hydration IS
      // the fetch. Hold it past the pageload's contended window — the lead
      // video, subtitles, and corpus payloads own the connection for the
      // first few seconds (the traced load's data tail ran to ~3.5s) — so
      // moving the transfer off the pageload actually moves it, instead of
      // re-entering the contention one second later. The cost: a funder who
      // hops to a proposal surface within these seconds on a cold client
      // sees placeholder text until the hydration lands — the deferral's
      // pre-existing tradeoff, extended by the delay. Warm clients (repeat
      // visits; the iPad) hold the text in IndexedDB and never notice.
      //
      // Funders only: the pathname alone put NON-funders on this deferred
      // path too (any viewer on a Review URL defers), but they don't stay —
      // the route guard redirects them onto proposal-rendering surfaces,
      // where the text this load skipped fetching is the content. For them
      // the hydration starts immediately, so the by-ids fetch is in flight
      // by the time the redirect lands.
      if (isReviewPathLoad && viewerIsFunder) {
        await new Promise((resolve) => setTimeout(resolve, 5_000));
      }
      for (let attempt = 0; ; attempt++) {
        // A superseded load (round change, impersonation switch, unmount) must
        // stop hydrating: the gc inside would sweep the global field-response
        // cache against a round nobody is viewing anymore, deleting the
        // replacement load's freshly cached text from IndexedDB.
        if (isAbandoned()) return;
        try {
          await hydrateFieldResponses();
          return;
        } catch (error) {
          if (attempt >= 2) {
            console.error("Deferred field-response hydration failed", error);
            Sentry.captureException(
              error instanceof Error
                ? error
                : new Error("Deferred field-response hydration failed"),
            );
            return;
          }
          await new Promise((resolve) =>
            setTimeout(resolve, 5_000 * (attempt + 1)),
          );
        }
      }
    })();
  } else {
    await hydrateFieldResponses();
  }

  // Reported here rather than inside the adapter, and only now: everything above
  // is part of the wait, and reaching this line is what makes the round usable.
  // An emit inside init would both under-report the duration and claim a
  // successful open before the field-response hydration above could fail. (On
  // routes where that hydration is deferred — Review URLs and funder entry
  // routes, see above — load_ms deliberately measures the shorter wait the
  // funder actually experiences there.)
  //
  // Carries the role explicitly instead of relying on the registered property:
  // registration happens after adapter init, so a super-property alone would
  // leave the first round open of a session unlabelled.
  if (!isAbandoned()) {
    const loadReport = storage.getLoadReport();
    // The load is over HERE — snapshot the duration now so nothing after it
    // (the detached storage probe included) can inflate the reported latency.
    const loadMs = Math.round(performance.now() - loadStartedAt);
    // DETACHED on purpose: this runs on loadRoundData's critical path, and a
    // slow navigator.storage.estimate() (plausible on exactly the
    // storage-pressured devices the field is for) must never hold a usable
    // round on the loading skeleton for telemetry's sake. The probe races a
    // short timeout so a hung estimate delays nothing and the event still
    // emits (with null storage fields). No abandonment re-check inside: the
    // guard above ran at the moment the round became usable — a later round
    // switch doesn't unhappen this open.
    void (async () => {
      // Device storage pressure (best effort; MB, rounded): quota-full
      // devices fail cache writes and degrade to downloads on every open —
      // this makes them visible BEFORE writes start failing. Safari's numbers
      // are approximate; the trend is the signal.
      const storageEstimate = await Promise.race([
        navigator.storage?.estimate?.().catch(() => null) ??
          Promise.resolve(null),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 3_000)),
      ]);
      const toMb = (bytes: number | undefined): number | null =>
        bytes == null ? null : Math.round(bytes / 1_000_000);
      captureRoundSyncEvent({
        event: ROUND_SYNC_EVENTS.roundOpened,
        properties: {
          round_id: roundId,
          load_mode: loadReport.loadMode,
          load_ms: loadMs,
          aggregate_version: agg.version,
          cache_first_eligible: loadReport.cacheFirstEligible,
          // How far behind the server the cached copy was (hits and version-gate
          // misses; null when no cache-first attempt ran) — the field data that
          // says where CACHE_FIRST_MAX_ACTION_GAP should sit.
          cache_gap_actions: loadReport.cacheFirstGap,
          // Why a cache-first attempt fell through (null on hits / no attempt).
          cache_miss_reason: loadReport.cacheFirstMissReason,
          storage_used_mb: toMb(storageEstimate?.usage),
          storage_quota_mb: toMb(storageEstimate?.quota),
          round_viewer_is_funder: viewerIsFunder,
        },
      });
    })();

    // Only when edits are actually stuck — an event on every clean open would
    // bury the cases that matter. `unshown_count` is the worst of them: edits the
    // funder made that could NOT be replayed onto the aggregate we loaded. The
    // offline banner tells THEM; until this, nothing told us.
    if (loadReport.pendingEdits != null) {
      captureRoundSyncEvent({
        event: ROUND_SYNC_EVENTS.offlineEditsPending,
        properties: {
          round_id: roundId,
          load_mode: loadReport.loadMode,
          queued_count: loadReport.pendingEdits.queuedCount,
          oldest_queued_age_ms: loadReport.pendingEdits.oldestQueuedAgeMs,
          unshown_count: loadReport.unshownOfflineEditCount,
          round_viewer_is_funder: viewerIsFunder,
        },
      });
    }
  }

  const client = new Client({
    id: clientId,
    userId: underlyingUserId,
    underlyingUserIsSfcAdmin: isSfcAdmin,
    aggregateId: roundId,
    onErrorCallback: handleError,
    onActionConfirmedCallback: (action) => {
      if (
        action.payload.type === "ADD_ROUND_MEMBER" ||
        action.payload.type === "CREATE_OR_UPDATE_ROUND_MEMBER"
      ) {
        refetchUserProfiles();
      }
    },
    reducer: roundReducer,
    storage,
    clientInfoStore: infoStore,
    captureRoundSyncEvent,
  });

  return {
    client,
    userProfiles,
    underlyingUserIsSfcAdmin: isSfcAdmin,
    viewerIsSpeculator,
  };
}

function RoundAdminContentLayout({
  roundId,
  children,
}: {
  roundId: string;
  children: ReactNode;
}) {
  const [location] = useLocation();
  const isAdminArea =
    location.startsWith("/admin") ||
    location === "/export-data" ||
    location.startsWith("/export-data/");
  return isAdminArea ? (
    <RoundAdminNavbar roundId={roundId}>{children}</RoundAdminNavbar>
  ) : (
    children
  );
}

const noop = () => {};

function ProposalsLoadingSkeleton() {
  return (
    <RouteScroller
      role="status"
      aria-label="Loading proposals"
      aria-busy="true"
    >
      <RoundPage
        title="Proposals"
        className="flex flex-col gap-6"
        cta={<Skeleton className="h-9 w-32 rounded-md" />}
      >
        <div className="dark flex flex-col gap-3">
          <ProposalsProgressCard completed={null} total={null} />
          <ProposalsStatusSummaryCards
            counts={{
              allActive: null,
              allInactive: null,
              hidden: null,
              notStarted: null,
              evaluated: null,
              canceled: null,
              recused: null,
              deferred: null,
              total: null,
            }}
            statusFilter={ALL_STATUSES}
            showHidden={false}
            onStatusFilterChange={noop}
            onShowHiddenChange={noop}
          />
        </div>
        <ProposalsFilters
          isRecommender
          sort="last-touched"
          onSortChange={noop}
          trackFilter={ALL_TRACK_CATEGORIES}
          onTrackFilterChange={noop}
          statusFilter={ALL_STATUSES}
          onStatusFilterChange={noop}
          showHidden={false}
          onShowHiddenChange={noop}
          searchQuery=""
          searchActive={false}
          onSearchChange={noop}
          hasActiveFilters={false}
          onClearFilters={noop}
        />
        <div className="rounded-lg border border-border-subtle bg-surface-base overflow-hidden">
          <TableEl containerClassName="bg-surface-base">
            <ProposalsTableHeader
              isRecommender
              sort="last-touched"
              sortDirection="desc"
              onSortChange={noop}
            />
          </TableEl>
          {/* Skeleton rows — the only part that needs data */}
          {Array.from({ length: 10 }, (_, i) => (
            <div
              key={i}
              className="border-b border-border-subtle px-4 py-3 last:border-b-0"
            >
              <Skeleton className="h-5 w-full max-w-md rounded" />
            </div>
          ))}
        </div>
      </RoundPage>
    </RouteScroller>
  );
}

function GenericLoadingSkeleton() {
  return (
    <RouteScroller className="p-4">
      <div className="mx-auto w-full max-w-content flex flex-col gap-6">
        <Skeleton className="h-8 w-48" />
        <div className="flex gap-3">
          <Skeleton className="h-9 w-40 rounded" />
          <Skeleton className="h-9 w-32 rounded" />
          <Skeleton className="h-9 flex-1 rounded" />
        </div>
        <div className="flex flex-col gap-2">
          {Array.from({ length: 12 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded" />
          ))}
        </div>
      </div>
    </RouteScroller>
  );
}

function ProposalDetailsLoadingSkeleton() {
  return (
    <div
      className="relative min-h-full p-4 flex mx-auto w-full max-w-content gap-6 flex-col overflow-x-clip"
      role="status"
      aria-label="Loading proposal details"
      aria-busy="true"
    >
      <div className="flex items-center gap-2">
        <span className="text-body-sm text-muted-foreground">Proposals</span>
        <span className="text-muted-foreground" aria-hidden="true">
          /
        </span>
        <Skeleton className="h-4 w-28" />
      </div>

      <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface-base shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 p-4 sm:p-6">
          <div className="min-w-0 flex-1 space-y-3">
            <Skeleton className="h-8 w-full max-w-xl" />
            <div className="flex gap-2">
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="h-5 w-24 rounded-full" />
            </div>
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-24" />
            <Skeleton className="h-9 w-20" />
          </div>
        </div>
        <div className="flex gap-2 border-t border-border-subtle px-4 py-2 sm:px-6">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-8 w-24" />
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)]">
        <div className="space-y-4">
          {Array.from({ length: 3 }, (_, index) => (
            <div
              key={index}
              className="space-y-3 rounded-lg border border-border-subtle bg-surface-base p-4 sm:p-6"
            >
              <Skeleton className="h-5 w-44" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-11/12" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          ))}
        </div>
        <div className="space-y-4">
          <div className="space-y-3 rounded-lg border border-border-subtle bg-surface-base p-4">
            <Skeleton className="h-5 w-36" />
            <Skeleton className="h-20 w-full" />
          </div>
          <div className="space-y-3 rounded-lg border border-border-subtle bg-surface-base p-4">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

function RoundLoadingSkeleton() {
  const [location] = useLocation();
  const isProposalDetails = location.startsWith("/proposals/");
  const isProposals = location === "/proposals";

  return isProposalDetails ? (
    <ProposalDetailsLoadingSkeleton />
  ) : isProposals ? (
    <ProposalsLoadingSkeleton />
  ) : (
    <GenericLoadingSkeleton />
  );
}

export function Round({ params }: { params: { roundId: string } }) {
  // Sync impersonation ref from localStorage before useImpersonationRefValue
  // reads it. This ensures loadRoundData sends the correct header on page
  // refresh with stored impersonation. Impersonation is app-scoped (not
  // round-scoped), so we read from the global key.
  const lastRestoredRoundRef = useRef<string | null>(null);
  if (lastRestoredRoundRef.current !== params.roundId) {
    lastRestoredRoundRef.current = params.roundId;
    impersonationRef.current = getGlobalImpersonation()?.userId ?? null;
  }

  const viewAsUserId = useImpersonationRefValue();

  const [loadState, dispatchLoadState] = useReducer(
    (
      state: {
        roundData: RoundData | null;
        error: ReturnType<typeof getRoundLoadErrorMessage> | null;
        refetchCount: number;
        /** Tracks which viewAsUserId the current roundData was loaded for. */
        loadedForViewAs: string | null;
      },
      action:
        | { type: "clearError" }
        | {
            type: "loadSuccess";
            roundData: RoundData;
            viewAsUserId: string | null;
          }
        | { type: "loadError"; error: unknown }
        | { type: "requestRefetch" },
    ) => {
      switch (action.type) {
        case "clearError":
          return state.error == null ? state : { ...state, error: null };
        case "loadSuccess":
          return {
            ...state,
            roundData: action.roundData,
            loadedForViewAs: action.viewAsUserId,
          };
        case "loadError":
          return { ...state, error: getRoundLoadErrorMessage(action.error) };
        case "requestRefetch":
          return { ...state, refetchCount: state.refetchCount + 1 };
        default:
          return state;
      }
    },
    {
      roundData: null,
      error: null,
      refetchCount: 0,
      loadedForViewAs: null,
    },
  );
  const { roundData, error, refetchCount, loadedForViewAs } = loadState;
  // Backoff for loads that never reached the server. Bounded and short: this
  // covers the seconds-long gap of a deploy or a dev backend restart, and then
  // hands the person a real error rather than spinning indefinitely.
  const autoRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keyed by what the load is FOR, so opening a different round (or switching
  // impersonation) starts with a full allowance instead of inheriting the
  // previous load's exhausted one.
  const autoRetryKey = `${params.roundId}:${viewAsUserId ?? ""}`;
  const autoRetryRef = useRef({ key: autoRetryKey, used: 0 });
  if (autoRetryRef.current.key !== autoRetryKey) {
    autoRetryRef.current = { key: autoRetryKey, used: 0 };
  }
  const clientIdRef = useRef(generateClientId());
  const clientId = clientIdRef.current;
  const queryClient = useQueryClient();
  const [roundLocation] = useLocation();
  // Dev-only "Disable round cache" override, admin-gated (SP-2637). Held in a
  // ref so the load effect can read the value in force at load time without
  // listing it as a dependency — flipping the toggle applies to the NEXT
  // round open and must not reload the round on the spot.
  const disableCachingActive = useDisableCachingActive();
  const disableCachingActiveRef = useRef(disableCachingActive);
  disableCachingActiveRef.current = disableCachingActive;

  // Player warming follows the nested route independently from round loading.
  // The default /round/:roundId landing redirects only after the bootstrap
  // identifies the viewer as a funder; keeping this inside the one-shot load
  // effect would snapshot the pre-redirect URL and permanently miss Review.
  useEffect(() => {
    if (workspaceMediaPathTargeted(params.roundId, window.location.pathname)) {
      preloadVideoPlayer();
    }
  }, [params.roundId, roundLocation]);

  // Show loading skeleton if impersonation changed since the last load completed.
  // Using reducer state (not a transient ref) so this persists across re-renders
  // until loadRoundData completes with data for the new viewAsUserId.
  const effectiveRoundData =
    loadedForViewAs !== viewAsUserId ? null : roundData;

  useEffect(() => {
    let cancelled = false;
    dispatchLoadState({ type: "clearError" });

    function refetchUserProfile() {
      dispatchLoadState({ type: "requestRefetch" });
    }

    // Deep links into the funder workspace's Overview/Track tabs need the
    // round's meeting list, and everything that query requires is already in
    // the URL — start it now, alongside the bootstrap, rather than after the
    // workspace renders. window.location (not wouter) on purpose: the round's
    // nested router isn't mounted yet, and a reactive location here would
    // re-run the whole round load on every in-round navigation.
    prefetchWorkspaceMeetings(
      queryClient,
      params.roundId,
      window.location.pathname,
      workspaceMeetingTargetFromSearch(window.location.search),
    );
    prefetchWorkspaceProposalClips(
      queryClient,
      params.roundId,
      window.location.pathname,
      workspaceExcerptTargetFromSearch(window.location.search),
    );
    prefetchWorkspaceRecommenderClips(
      queryClient,
      params.roundId,
      window.location.pathname,
      workspaceExcerptTargetFromSearch(window.location.search),
    );
    prefetchWorkspaceCorpus(
      queryClient,
      params.roundId,
      window.location.pathname,
    );

    void loadRoundData(
      clientId,
      params.roundId,
      refetchUserProfile,
      queryClient,
      () => cancelled,
      // Read through a ref at load time, NOT an effect dependency: flipping
      // the dev toggle must change the next open, never reload the round the
      // admin is looking at (same "applies when next opened" stance as the
      // Force streaming toggle).
      disableCachingActiveRef.current,
    )
      .then((data) => {
        if (cancelled) return;
        // A load that reached the server spends the allowance it borrowed: the
        // budget is three attempts per outage, not three for the lifetime of
        // the page. Without this, a deploy blip that burned two retries and
        // then recovered would leave the next unreachable moment on this round
        // with a single attempt before it gave up on the funder.
        autoRetryRef.current.used = 0;
        dispatchLoadState({
          type: "loadSuccess",
          roundData: data,
          viewAsUserId,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        // A load that never got an answer retries itself before it becomes a
        // screen the funder has to click through. This is the same "Try again"
        // the button dispatches — the person just doesn't have to be the one
        // to notice. Errors the server SENT (no access, no such round) skip
        // this entirely and surface at once.
        if (
          (isUnreachableRoundLoadError(err) || isTransportError(err)) &&
          autoRetryRef.current.used < AUTO_RETRY_DELAYS_MS.length
        ) {
          const delayMs = AUTO_RETRY_DELAYS_MS[autoRetryRef.current.used]!;
          autoRetryRef.current.used += 1;
          autoRetryTimerRef.current = setTimeout(() => {
            if (cancelled) return;
            dispatchLoadState({ type: "requestRefetch" });
          }, delayMs);
          return;
        }
        dispatchLoadState({
          type: "loadError",
          error: err,
        });
        console.error("Error loading round:", err);
      });

    return () => {
      cancelled = true;
      if (autoRetryTimerRef.current != null) {
        clearTimeout(autoRetryTimerRef.current);
        autoRetryTimerRef.current = null;
      }
    };
  }, [params.roundId, clientId, refetchCount, viewAsUserId, queryClient]);

  if (!effectiveRoundData) {
    return (
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {error ? (
          <RouteScroller>
            <Container>
              <div className="flex flex-col items-center justify-center gap-2 py-8">
                <p className="text-red-500">{error.title}</p>
                <p className="max-w-lg text-center text-muted-foreground">
                  {error.description}
                </p>
                {error.retryable && (
                  <Button
                    className="mt-2"
                    onClick={() => {
                      // A deliberate press restores the automatic allowance too:
                      // the person is telling us to treat this as a fresh
                      // attempt, and the earlier failures may be minutes old.
                      autoRetryRef.current = { key: autoRetryKey, used: 0 };
                      // Re-runs the load effect (refetchCount is one of its
                      // deps); the effect's leading clearError restores the
                      // loading skeleton while the retry runs.
                      dispatchLoadState({ type: "requestRefetch" });
                    }}
                  >
                    Try again
                  </Button>
                )}
                <a className="text-primary underline" href="/">
                  Go to homepage
                </a>
              </div>
            </Container>
          </RouteScroller>
        ) : (
          <RoundLoadingSkeleton />
        )}
      </main>
    );
  }

  // Key the provider AND the wrapper on the `(roundId, viewAsUserId)`
  // tuple. Two reasons we can't rely on just one of them:
  //  - viewAsUserId in `VideoStatesProvider`'s tRPC query input keeps
  //    React Query's cache entries separate per impersonation target.
  //  - The `key` on the provider itself remounts it on impersonation
  //    switch, which tears down the in-flight `writeMutation` observer
  //    (whose `onSuccess` would otherwise close over the new
  //    queryInput and write the previous user's response into the new
  //    user's cache) and cancels the throttle map (whose pending
  //    callbacks would otherwise fire `performWrite` with the new
  //    queryInput).
  const videoStatesKey = `${params.roundId}-${viewAsUserId ?? "self"}`;

  return (
    <VideoStatesProvider
      key={videoStatesKey}
      roundId={params.roundId}
      viewAsUserId={viewAsUserId ?? null}
    >
      <RoundWrapper
        key={videoStatesKey}
        roundData={effectiveRoundData}
        roundId={params.roundId}
      />
    </VideoStatesProvider>
  );
}

export const AlgorithmIsRunningContext = createContext<boolean>(false);

function BasePathRedirector({ roundId }: { roundId: string }) {
  const round = useRound();
  const userId = useActingAsUserId();
  const effectiveIsSfcAdmin = useEffectiveIsSfcAdmin();
  const isRestrictedFunder = useIsRestrictedFunder();

  if (!userId || !roundId) return null; // Still loading or user/round ID not found

  const roundMember = round.members[userId];
  const isRoundAdmin = roundMember
    ? roundMember.isRoundAdmin
    : effectiveIsSfcAdmin;
  const memberTrack = roundMember?.track;

  const trackStatus =
    memberTrack != null && memberTrack !== "NO_TRACK"
      ? round.trackSettings[memberTrack].status
      : undefined;

  // `replace` so /round/:roundId — which is router-only, never a page — doesn't
  // leave a no-op entry in history that the Back chevron lands on and bounces
  // off of (which would re-trigger this same redirect).
  if (!round.isSfcRound) {
    return <Redirect to="/proposals" replace />;
  }

  // funder probably wants to go to their workspace first
  if (isUserFunder(round, userId)) {
    return (
      <Redirect
        to={isRestrictedFunder ? "/funder/decide" : "/funder/review"}
        replace
      />
    );
  }

  if (isRoundAdmin || memberTrack === "NO_TRACK") {
    return <Redirect to="/admin/recommender-activity" replace />;
  }

  if (trackStatus === "1.onboarding") {
    return <Redirect to="/onboarding" replace />;
  } else if (trackStatus === "2.initial-evaluations") {
    const evaluator = getActiveEvaluators(round).find(
      (evaluator) => evaluator.memberId === userId,
    );
    return (
      <Redirect to={evaluator ? "/proposals" : "/investigations"} replace />
    );
  } else if (
    trackStatus === "3.full-discussion" ||
    trackStatus === "4.evals-freeze" ||
    trackStatus === "5.complete"
  ) {
    return <Redirect to="/proposals" replace />;
  }

  return null;
}

function FunderBareRedirect() {
  const isRestrictedFunder = useIsRestrictedFunder();
  return (
    <Redirect to={isRestrictedFunder ? "/funder/decide" : "/funder/review"} />
  );
}

function FunderRouteGuard({ children }: { children: ReactNode }) {
  const round = useRound();
  const actingAsUserId = useActingAsUserId();
  const effectiveIsSfcAdmin = useEffectiveIsSfcAdmin();

  if (!actingAsUserId) return null;

  const canAccess = effectiveIsSfcAdmin || isUserFunder(round, actingAsUserId);
  if (!canAccess) {
    return <Redirect to="/" />;
  }

  return <>{children}</>;
}

// Backward-compat for the removed /funder/materials page, which used ?org=,
// ?rec=, and ?tab= query params for selection. Map those onto the workspace's
// path-based Review routes so old bookmarks and shared links still land on
// the right proposal/recommender.
function FunderMaterialsRedirect() {
  const [searchParams] = useSearchParams();
  const isRestrictedFunder = useIsRestrictedFunder();

  if (isRestrictedFunder) {
    return <Redirect to="/funder/decide" replace />;
  }

  if (searchParams.get("tab") === "recs") {
    const rec = searchParams.get("rec");
    return (
      <Redirect
        to={
          rec
            ? `/funder/review/recommenders/${encodeURIComponent(rec)}`
            : "/funder/review/recommenders"
        }
        replace
      />
    );
  }

  const org = searchParams.get("org");
  return (
    <Redirect
      to={
        org
          ? `/funder/review/proposals/${encodeURIComponent(org)}`
          : "/funder/review"
      }
      replace
    />
  );
}

// Backward-compat for the funder workspace's Review tab, which used to live at
// `/funder/understand`. Redirect old bookmarks and shared passage links (which
// carry deep-link params like ?proposal=, ?quote=, and #passage=) to the new
// `/funder/review` path, preserving everything after the tab segment.
function FunderUnderstandRedirect() {
  const [location] = useLocation();
  const isRestrictedFunder = useIsRestrictedFunder();

  if (isRestrictedFunder) {
    return <Redirect to="/funder/decide" replace />;
  }

  const rest = location.replace(/^\/funder\/understand/, "");
  return (
    <Redirect
      to={`/funder/review${rest}${window.location.search}${window.location.hash}`}
      replace
    />
  );
}

// Backward-compat for the Note Ratings subview's former URL segments. Keep old
// bookmarks and shared links working while making `note-ratings` the one URL
// every current navigation surface emits. Preserve filters and deep-link
// fragments, and replace the history entry so Back does not bounce through the
// legacy URL again.
function FunderLegacyNoteRatingsRedirect() {
  const [location] = useLocation();
  const rest = location.replace(
    /^\/funder\/decide\/(?:notes-ratings|ratings)/,
    "",
  );
  return (
    <Redirect
      to={`/funder/decide/note-ratings${rest}${window.location.search}${window.location.hash}`}
      replace
    />
  );
}

function RoundNotFoundPage({ roundId }: { roundId: string }) {
  const [location] = useLocation();
  const currentPath =
    location === "/" ? `/round/${roundId}` : `/round/${roundId}${location}`;

  return (
    <RoundPage
      title="Page not found"
      description="That link doesn't match a page in this round."
    >
      <NotFoundCard
        currentPath={currentPath}
        primaryHref={`/round/${roundId}`}
        primaryLabel="Go to round home"
        secondaryHref="/"
        secondaryLabel="Go to homepage"
      />
    </RoundPage>
  );
}

function OutputContextProvider({ children }: { children: ReactNode }) {
  // Narrow subscription: only the round feeds the algorithm. `children` comes
  // from the render-inert shell, so a round-driven re-render here stops at
  // this provider — consumers re-render only when the output state changes.
  const round = useRound();
  const [outputState, dispatchOutputState] = useReducer(
    (
      current: {
        output: SProcessOutputWithMatchingAdjustment | null;
        outputRound: RoundAggregate | null;
        algorithmIsRunning: boolean;
      },
      action:
        | { type: "startComputation" }
        | {
            type: "finishComputation";
            output: SProcessOutputWithMatchingAdjustment;
            round: RoundAggregate;
          },
    ) => {
      switch (action.type) {
        case "startComputation":
          return { ...current, algorithmIsRunning: true };
        case "finishComputation":
          return {
            output: action.output,
            outputRound: action.round,
            algorithmIsRunning: false,
          };
        default:
          return current;
      }
    },
    null,
    () => ({
      // The first run is deferred to the idle effect below: the round's first
      // render is already the longest main-thread task of the load, and
      // running the algorithm inside this initializer made it pay ~100ms more
      // before anything could paint. Consumers handle the missing output the
      // same way they handle a round that isn't ready to evaluate.
      output: null as SProcessOutputWithMatchingAdjustment | null,
      outputRound: null as RoundAggregate | null,
      algorithmIsRunning: true,
    }),
  );
  const prevRoundRef = useRef<RoundAggregate>(round);

  // The idle callback below fires after renders that may have replaced the
  // round, so it reads the round through a ref to compute the current version.
  const roundRef = useRef<RoundAggregate>(round);
  useEffect(() => {
    roundRef.current = round;
  }, [round]);

  // First algorithm run, once the browser has painted the bootstrap render
  // and gone idle (or after the timeout cap if it stays busy). startTransition
  // keeps the resulting re-render interruptible so allocations landing can't
  // block input. Recomputations after round changes are handled by the effect
  // below; if one races this callback, both resolve to the same cached output.
  useEffect(() => {
    return runWhenIdle(
      () =>
        startTransition(() =>
          dispatchOutputState({
            type: "finishComputation",
            output: sProcessOutputForRound(roundRef.current),
            round: roundRef.current,
          }),
        ),
      { timeout: 500 },
    );
  }, []);

  // Used to cancel the previous computation if it's still running.
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Re-compute the S-Process result exactly once whenever the round’s decisive
  // identifiers change (version or latestActionId). This effect will *not* run
  // for unrelated state updates, so the expensive calculation is executed only
  // when necessary.
  useEffect(() => {
    if (!roundsCouldDifferInResult(prevRoundRef.current, round)) {
      return;
    }
    prevRoundRef.current = round;

    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
    }

    dispatchOutputState({ type: "startComputation" });

    timeoutRef.current = setTimeout(() => {
      dispatchOutputState({
        type: "finishComputation",
        output: sProcessOutputForRound(round),
        round,
      });
      timeoutRef.current = null;
    }, 100);
  }, [round]);

  return (
    <OutputRoundContext.Provider value={outputState.outputRound}>
      <OutputContext.Provider value={outputState.output}>
        <AlgorithmIsRunningContext.Provider
          value={outputState.algorithmIsRunning}
        >
          {children}
        </AlgorithmIsRunningContext.Provider>
      </OutputContext.Provider>
    </OutputRoundContext.Provider>
  );
}

/**
 * The round shell's cross-cutting effects, in a render-null component so the
 * shell (RoundWrapper) itself never subscribes to the store. This is the one
 * place that deliberately subscribes to the WHOLE state: it renders nothing,
 * so its per-emission re-render costs only these effects' dep checks — and
 * that per-emission render is what lets the drain effect below fire action
 * events after the render they belong to (see emitAction in ImperativeState).
 */
function RoundShellEffects({
  roundData,
  roundId,
  stateStore,
  drainPendingActions,
}: {
  roundData: RoundData;
  roundId: string;
  stateStore: StateExternalStore;
  drainPendingActions: () => void;
}) {
  const state = useSyncExternalStore(
    stateStore.subscribe,
    stateStore.getSnapshot,
  );
  const [location] = useLocation();
  const {
    socketStatus,
    clientId,
    round,
    unconfirmedActions,
    retryUnconfirmedActions,
    clearUnconfirmedActions,
    reportError,
    storage,
    disconnect,
  } = state;

  // Flush the action queue after every store-driven render (see emitAction).
  useEffect(() => {
    drainPendingActions();
  });

  // General-note edits change consistency ratings server-side without moving
  // any client-computed hash; refetch the cached ratings when one changes.
  useMvfGeneralNotesRatingRefresh(round.members);

  const setHeaderConnection = useSetHeaderConnection();
  useEffect(() => {
    setHeaderConnection({
      socketStatus,
      schemaSkew: state.schemaSkew,
      sessionRejected: state.sessionRejected,
      clientId,
      round,
      unconfirmedActions,
      retryUnconfirmedActions,
      clearUnconfirmedActions,
      reportError,
      storage,
      connectedUserIds: state.connectedUserIds,
    });
    return () => setHeaderConnection(null);
  }, [
    setHeaderConnection,
    socketStatus,
    state.schemaSkew,
    state.sessionRejected,
    clientId,
    round,
    unconfirmedActions,
    retryUnconfirmedActions,
    clearUnconfirmedActions,
    reportError,
    storage,
    state.connectedUserIds,
  ]);
  const undoToastIds = useRef<{ [actionId: string]: ToastT["id"] }>({});
  const { engineIsOutOfDate } = useIsOutOfDate();
  const hasDisconnectedForOutOfDateRef = useRef(false);

  useEffect(() => {
    const roundLabel =
      state.round.name.trim().length > 0
        ? state.round.name
        : `Round ${roundId}`;
    document.title = getDocumentTitleForRoundSubPath(location, roundLabel);
  }, [location, roundId, state.round.name]);

  useEffect(() => {
    if (engineIsOutOfDate && !hasDisconnectedForOutOfDateRef.current) {
      disconnect("stale-build");
      hasDisconnectedForOutOfDateRef.current = true;
      return;
    }

    if (!engineIsOutOfDate && hasDisconnectedForOutOfDateRef.current) {
      hasDisconnectedForOutOfDateRef.current = false;
    }
  }, [engineIsOutOfDate, disconnect]);

  // Message schema skew: the wrapper already disconnected the socket (see
  // onSchemaSkew in ImperativeState); this only tells the user what to do.
  // Session death mid-round is otherwise invisible: HTTP keeps serving cached
  // queries, so the workspace looks alive while nothing syncs (SP-2590).
  const hasShownSessionRejectedToastRef = useRef(false);
  const sessionRejected = state.sessionRejected;
  useEffect(() => {
    if (!sessionRejected || hasShownSessionRejectedToastRef.current) {
      return;
    }
    hasShownSessionRejectedToastRef.current = true;
    toast.warning(
      isIPadCapacitor() ? (
        <div>
          Your session has expired, so changes can no longer sync. Close and
          reopen the app to sign in again — changes saved on this iPad are kept.
        </div>
      ) : (
        <div>
          Your session has expired, so changes can no longer sync. Please{" "}
          <button
            type="button"
            className="underline cursor-pointer"
            onClick={() => window.location.reload()}
          >
            refresh
          </button>{" "}
          and sign in again.
        </div>
      ),
      { duration: Infinity },
    );
  }, [sessionRejected]);

  // The build-id poll can't catch this case — a stale bundle that loads
  // after a deploy records the server's build as its own — so the notice
  // must come from the failed parse itself.
  const hasShownSchemaSkewToastRef = useRef(false);
  const schemaSkew = state.schemaSkew;
  useEffect(() => {
    if (!schemaSkew || hasShownSchemaSkewToastRef.current) {
      return;
    }

    hasShownSchemaSkewToastRef.current = true;
    toast.warning(
      isIPadCapacitor() ? (
        <div>
          This version of the app can no longer sync this round. Please update
          the app from TestFlight, then reopen it.
        </div>
      ) : (
        <div>
          This page is running an outdated version and can no longer sync.
          Please{" "}
          <button
            type="button"
            className="underline cursor-pointer"
            onClick={() => window.location.reload()}
          >
            refresh
          </button>{" "}
          the page.
        </div>
      ),
      { duration: Number.POSITIVE_INFINITY },
    );
  }, [schemaSkew]);

  useEffect(() => {
    const isMac = navigator.platform.includes("Mac");
    const undoKey = isMac ? "⌘Z" : "Ctrl+Z";
    const redoKey = isMac ? "⌘⇧Z" : "Ctrl+Shift+Z";

    // Undo and redo reach the engine client directly rather than through
    // ClientWrapper.dispatch, so they honour the edit lock here — read at
    // press time, not captured, since the lock can change mid-session.
    function refuseIfLocked(): boolean {
      const lock = stateStore.getSnapshot().editLock;
      if (!lock) return false;
      // Any lock refuses. Undo has no inverse for the archive action (see
      // round/undo.ts), so under the archived lock ⌘Z would skip the archive
      // and pick an earlier edit the reducer then refuses; restoring is the
      // Restore button's job.
      toast.error(lock.message);
      return true;
    }

    async function handleUndo() {
      if (refuseIfLocked()) return;
      const result = await roundData.client.dispatchUndo();
      if (result instanceof Error) {
        console.error("Undo failed", result);
        toast.error(
          <div>
            <div>Undo ({undoKey}) failed</div>
            <div>{result.message}</div>
          </div>,
        );
      } else if (result === null) {
        return; // No undoable actions -- don't show a message or take any action
      } else {
        const { message, originalAction } = result;

        // Don't show incremental evals toasts (SP-792)
        if (!originalAction.payload.type.startsWith("INCREMENTAL_EVALS_")) {
          const cleanupToast = () => {
            delete undoToastIds.current[message.id];
          };
          toast.success(`Undo (${undoKey}) of event successful:`, {
            description: originalAction
              ? humanizeActionPayload(round, originalAction.payload)
              : undefined,
            action: {
              label: `Redo (${redoKey})`,
              onClick: () => {
                void handleRedo();
              },
            },
            id: message.id,
            onDismiss: cleanupToast,
            onAutoClose: cleanupToast,
          });
          undoToastIds.current[message.id] = message.id;
        }
      }
    }

    async function handleRedo() {
      if (refuseIfLocked()) return;
      const result = await roundData.client.dispatchRedo();
      if (result instanceof Error) {
        toast.error(
          <div>
            <div>Redo ({redoKey}) failed</div>
            <div>{result.message}</div>
          </div>,
        );
      } else if (result === null) {
        return; // No redoable actions -- don't show a message or take any action
      } else {
        const { message, originalAction } = result;
        if (
          message.redoes != null &&
          message.undoes != null &&
          // Don't show incremental evals toasts (SP-792)
          !originalAction.payload.type.startsWith("INCREMENTAL_EVALS_")
        ) {
          const originalActionSummary = originalAction
            ? humanizeActionPayload(round, originalAction.payload)
            : null;
          toast.success(`Redo (${redoKey}) of event successful:`, {
            description: originalActionSummary ?? undefined,
          });
          const undoActionId = message.undoes;
          if (undoToastIds.current[undoActionId]) {
            const toastId = undoToastIds.current[undoActionId];
            toast.success(`Undo (${undoKey}) of event successful:`, {
              id: toastId,
              action: undefined,
              description: originalActionSummary ?? undefined,
            });
          }
        }
      }
    }

    async function keydownHandler(e: KeyboardEvent) {
      const isUndoRedo =
        (e.key === "z" || e.key === "Z") && (e.ctrlKey || e.metaKey);
      if (!isUndoRedo) {
        return;
      }

      const active = document.activeElement;
      if (isTextEditingElement(active) || isTextEditingElement(e.target))
        return;

      e.preventDefault();

      if (e.shiftKey) {
        await handleRedo();
      } else {
        await handleUndo();
      }
    }

    const wrappedHandler = (e: KeyboardEvent) => {
      void keydownHandler(e);
    };
    document.addEventListener("keydown", wrappedHandler);
    return () => {
      document.removeEventListener("keydown", wrappedHandler);
    };
  }, [roundData.client, round, stateStore]);

  return null;
}

// The per-route components below read round state through the slice hooks so
// the shell's Switch can stay state-free. Each re-renders on round changes
// (or, for the isSfcRound-only ones, effectively never) instead of the whole
// routed subtree re-rendering on every emission.

function ProposalDetailsRoute({ proposalId }: { proposalId: string }) {
  const round = useRound();
  const resolvedProposalId = resolveProposalIdFromRouteSegment(
    round,
    proposalId,
  );

  if (resolvedProposalId == null) {
    return <Redirect to="/proposals" />;
  }

  return (
    <div
      key={`proposal-${resolvedProposalId}`}
      className="flex-1 flex flex-col overflow-hidden"
    >
      <RouteScroller
        className="[overflow-anchor:none]"
        data-proposal-scroll-container
      >
        <Suspense fallback={<ProposalDetailsLoadingSkeleton />}>
          <ProposalDetailsPage proposalId={resolvedProposalId} />
        </Suspense>
      </RouteScroller>
    </div>
  );
}

function ProposalsListRoute() {
  const isSfcRound = useStateSelector((state) => state.round.isSfcRound);
  return isSfcRound ? (
    <div key="proposals-list" className="flex-1 flex flex-col overflow-hidden">
      <RouteScroller data-proposals-list-scroll-container>
        <Suspense fallback={<ProposalsLoadingSkeleton />}>
          <ProposalsPage />
        </Suspense>
      </RouteScroller>
    </div>
  ) : (
    <div className="flex-1 flex flex-col overflow-hidden">
      <RouteScroller className="flex overflow-x-auto">
        <Suspense fallback={<ProposalsLoadingSkeleton />}>
          <ProposalsTablePage />
        </Suspense>
      </RouteScroller>
    </div>
  );
}

function RoundWrapper({
  roundData,
  roundId,
}: {
  roundData: RoundData;
  roundId: string;
}) {
  // Deliberately subscription-free: this shell renders the entire routed
  // round subtree, so a store subscription here would parent-render every
  // mounted round screen on every emission and defeat the per-field selector
  // hooks below it. State reads live in RoundShellEffects (render-null), the
  // shell providers (stable children), and per-route components.
  const [dispatch, stateStore, drainPendingActions] =
    useEventSourcing(roundData);
  const stateContextStore = useMemo(
    () => createStateContextStore(stateStore),
    [stateStore],
  );

  // Covers action events raised around the shell's own (rare) renders; the
  // per-emission drain is RoundShellEffects'.
  useEffect(() => {
    drainPendingActions();
  });

  return (
    <SocketStatusProvider stateStore={stateStore}>
      <StateContext.Provider value={stateContextStore}>
        <RoundShellEffects
          roundData={roundData}
          roundId={roundId}
          stateStore={stateStore}
          drainPendingActions={drainPendingActions}
        />
        <ViewerRoleSync />
        <ImpersonateSync />
        <DispatchContext.Provider value={dispatch}>
          <ClientSettingsProvider>
            <PinnedNodesProvider>
              <OutputContextProvider>
                {/* Owns the To Do rail's durable write queues, so a mark made
                    offline survives until reconnect. Wraps the round subtree
                    because both the notes drawer and the workspace panels
                    enqueue through it. */}
                <TodoRailSyncProvider roundId={roundId}>
                  <GeneralNotesDrawer />
                  {/* Peer of the notes drawer: opened from the app bar through
                      the shared drawer store, but rendered here because the
                      rail is derived from the round aggregate. */}
                  <TodoDrawer roundId={roundId} />
                  {/* One boundary for every lazily-split route above. Routes
                    without a stable shared shape retain the generic content
                    fallback. Landing-critical proposal routes add their own
                    boundaries below so they preserve their page structure
                    while their chunks load. */}
                  <Suspense fallback={<div className="flex-1" />}>
                    <RouteErrorBoundary
                      homeHref={`~/round/${roundId}`}
                      homeLabel="Back to round"
                    >
                      <Switch>
                        {/* Evaluations page without sidebar */}
                        <Route path="/proposals/:proposalId">
                          {({ proposalId }) => (
                            <ProposalDetailsRoute proposalId={proposalId} />
                          )}
                        </Route>
                        <Route path="/proposals">
                          {() => <ProposalsListRoute />}
                        </Route>
                        <Route path="/comparisons/:recommenderInitials">
                          {(params) => <ComparisonsPage params={params} />}
                        </Route>
                        <Route
                          path="/comparisons"
                          component={ComparisonsPage}
                        />
                        <Route
                          path="/disagreements"
                          component={DisagreementsPage}
                        />
                        <Route
                          path="/funding-flow"
                          component={FundingFlowMatrixPage}
                        />
                        <Route path="/funder/materials">
                          {() => (
                            <FunderRouteGuard>
                              <FunderMaterialsRedirect />
                            </FunderRouteGuard>
                          )}
                        </Route>
                        <Route path="/funder/understand/:rail?/:itemId?">
                          {() => <FunderUnderstandRedirect />}
                        </Route>
                        <Route path="/funder/decide/notes-ratings/:itemId?">
                          {() => <FunderLegacyNoteRatingsRedirect />}
                        </Route>
                        <Route path="/funder/decide/ratings/:itemId?">
                          {() => <FunderLegacyNoteRatingsRedirect />}
                        </Route>
                        <Route path="/funder/:tab/:rail?/:itemId?">
                          {() => (
                            <FunderRouteGuard>
                              <div className="flex-1 flex flex-col overflow-hidden">
                                <div className="flex-1 min-h-0 overflow-hidden">
                                  <FunderWorkspacePage />
                                </div>
                              </div>
                            </FunderRouteGuard>
                          )}
                        </Route>
                        <Route path="/funder">
                          {() => <FunderBareRedirect />}
                        </Route>

                        {/* All other pages with sidebar */}
                        <Route>
                          {() => <RoundAdminContentRoute roundId={roundId} />}
                        </Route>
                      </Switch>
                    </RouteErrorBoundary>
                  </Suspense>
                </TodoRailSyncProvider>
              </OutputContextProvider>
            </PinnedNodesProvider>
          </ClientSettingsProvider>
        </DispatchContext.Provider>
      </StateContext.Provider>
    </SocketStatusProvider>
  );
}

/**
 * Scroller for admin-content pages that let the shell own their scroll
 * container (Onboarding, Investigations, Recusals, Speculation Grants).
 * Back/Forward (including the iPad header's back chevron) returns to the
 * spot the reader left, not the top of the page.
 *
 * The div is keyed by pathname because sibling routes all render this same
 * component at the same tree position, so React would otherwise reuse one
 * DOM node across a sidebar switch and the new page would inherit the old
 * page's scroll offset. Keyed remount puts a push navigation at the top;
 * history pops still land on the saved offset via the restoration hook.
 */
function RestoredScrollPane({
  roundId,
  children,
}: {
  roundId: string;
  children: ReactNode;
}) {
  const [pathname] = useLocation();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  usePageScrollRestoration({
    roundId,
    getContainer: () => scrollerRef.current,
  });
  return (
    <RouteScroller key={pathname} ref={scrollerRef}>
      {children}
    </RouteScroller>
  );
}

/**
 * The admin/sidebar fallback route. Owns the inner Switch so its
 * isSfcRound-dependent route set can subscribe narrowly here instead of the
 * shell subscribing for it (wouter's Switch needs Route elements as direct
 * children, so the conditional can't sit behind a component boundary inside
 * the Switch itself).
 */
function RoundAdminContentRoute({ roundId }: { roundId: string }) {
  const isSfcRound = useStateSelector((state) => state.round.isSfcRound);
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <RoundAdminContentLayout roundId={roundId}>
        {/* Nested boundary: a suspending lazy admin page
                                must blank only the CONTENT region — without
                                this, the outer route boundary would tear
                                down the shared admin sidebar/navbar on every
                                first navigation into an admin chunk. */}
        <Suspense fallback={<div className="flex-1" />}>
          <Switch>
            <Route path="/">
              {() => <BasePathRedirector roundId={roundId} />}
            </Route>
            <Route path="/members" component={MembersPage} />
            {isSfcRound ? (
              <>
                <Route path="/admin/track-settings">
                  {() => <TrackSettingsPage />}
                </Route>
                <Route path="/admin/track-settings/:track">
                  {({ track }) => <TrackSettingsPage routeTrackSlug={track} />}
                </Route>
                <Route path="/admin/round-members" component={RoundMembers} />
                <Route
                  path="/admin/round-settings"
                  component={SfcRoundSettings}
                />
                <Route
                  path="/admin/recommender-activity"
                  component={RecommenderActivityPage}
                />
                <Route
                  path="/admin/fact-check-responses"
                  component={FactCheckResponsesPage}
                />
                <Route path="/admin/data-syncing" component={DataSyncing} />
                <Route path="/admin/proposal-forms" component={ProposalForms} />
                <Route
                  path="/admin/anonymization"
                  component={AnonymizationPage}
                />
                <Route path="/admin/logs" component={LogsRoute} />
                <Route path="/export-data" component={ExportData} />
                <Route path="/admin/impersonate" component={Settings} />
                <Route path="/speculation-grants">
                  {() => (
                    <RestoredScrollPane roundId={roundId}>
                      <SpeculationGrants />
                    </RestoredScrollPane>
                  )}
                </Route>
                <Route path="/onboarding">
                  {() => (
                    <RestoredScrollPane roundId={roundId}>
                      <Onboarding />
                    </RestoredScrollPane>
                  )}
                </Route>
                <Route path="/investigations">
                  {() => (
                    <RestoredScrollPane roundId={roundId}>
                      <Investigations />
                    </RestoredScrollPane>
                  )}
                </Route>
                <Route path="/recusals">
                  {() => (
                    <RestoredScrollPane roundId={roundId}>
                      <Recusals />
                    </RestoredScrollPane>
                  )}
                </Route>
              </>
            ) : (
              <>
                <Route path="/settings/round" component={RoundSettings} />
                <Route path="/settings/funder" component={FunderSettings} />
              </>
            )}
            <Route path="*">
              {() => <RoundNotFoundPage roundId={roundId} />}
            </Route>
          </Switch>
        </Suspense>
      </RoundAdminContentLayout>
    </div>
  );
}
